import { useTranslation } from "react-i18next";
import { Layout } from "@layouts";
import { Input, Spinner, Button, ApproveTransactionButton, RejectTransactionButton } from "@atoms";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button as ShadcnButton,
} from "@shadcn";
import { Bar } from "@organisms";
import { PageBreadcrumbs } from "@molecules";
import { 
  Check,
  X,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RotateCcw
} from "lucide-react";
import { 
  LSATransaction, 
  LSATransactionStatus, 
  TransactionFilter, 
  PaginationInfo,
  LSACategory
} from "../../../../types";
import { AuthContext } from "@auth";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { PagePaths } from "../../../../types";
import ReactModal from "react-modal";

const colors = require("@theme/colors") as Record<string, any>;
export type LifestyleTransactionsPageTemplateProps = {
  transactions: LSATransaction[];
  pagination: PaginationInfo;
  isInitialLoading: boolean;
  isPageLoading: boolean;
  error: string | null;
  selectedTab: LSATransactionStatus | 'all';
  setSelectedTab: (tab: LSATransactionStatus | 'all') => void;
  filters: TransactionFilter;
  setFilters: (filters: TransactionFilter) => void;
  onApproveTransaction: (transactionId: string, rowIndex: number) => Promise<void>;
  onRejectTransaction: (transactionId: string, rowIndex: number) => void;
  approvingTransactionIds: Set<string>;
  rejectingTransactionIds: Set<string>;
  activeRowId: string | null;
  activeRowIndex: number | null;
  approveErrorMessage: string | null;
  onDismissApproveError: () => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onReturnPressed: () => void;
  onRefresh: () => Promise<void>;
};

export const LifestyleTransactionsPageTemplate = ({
  transactions,
  pagination,
  isInitialLoading,
  isPageLoading,
  error,
  selectedTab: _selectedTab,
  setSelectedTab: _setSelectedTab,
  filters,
  setFilters,
  onApproveTransaction,
  onRejectTransaction,
  approvingTransactionIds,
  rejectingTransactionIds,
  activeRowId,
  activeRowIndex: _activeRowIndex,
  approveErrorMessage,
  onDismissApproveError,
  sortField: _sortField,
  sortDirection: _sortDirection,
  onSort: _onSort,
  onPageChange,
  onReturnPressed,
  onRefresh,
}: LifestyleTransactionsPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const { lifestyleCategories } = useContext(AuthContext);
  const navigate = useNavigate();
  const goToPerks = () => navigate(`/dashboard/${PagePaths.perks}`);
  const goToLifestyleMain = () => navigate(`/dashboard/${PagePaths.lifestyle}/main`);
  type StatusFilterValue = 'all' | LSATransactionStatus.Completed | LSATransactionStatus.PendingApproval | LSATransactionStatus.Incomplete;
  const statusFilterValue: StatusFilterValue = (filters?.status as StatusFilterValue) ?? 'all';
  const hasActionInFlight =
    approvingTransactionIds.size > 0 ||
    rejectingTransactionIds.size > 0 ||
    activeRowId !== null;

  const statusFilterOptions: { label: string; value: StatusFilterValue }[] = [
    { value: 'all', label: t("lifestyleTransactions.statusOptions.all") },
    { value: LSATransactionStatus.Completed, label: t("lifestyleTransactions.statusOptions.completed") },
    { value: LSATransactionStatus.PendingApproval, label: t("lifestyleTransactions.statusOptions.pending") },
    { value: LSATransactionStatus.Incomplete, label: t("lifestyleTransactions.statusOptions.incomplete") },
  ];

  const selectedStatusOption =
    statusFilterOptions.find((option) => option.value === statusFilterValue) ??
    statusFilterOptions[0];

  const handleStatusFilterChange = (option: { value: StatusFilterValue } | null) => {
    const value = option?.value ?? 'all';

    if (value === 'all') {
      if (filters?.status) {
        const { status: _status, ...rest } = filters;
        setFilters({
          ...rest,
        });
      }
      onPageChange(1);
      return;
    }

    setFilters({
      ...(filters ?? {}),
      status: value,
    });
    onPageChange(1);
  };

  const shouldShowTransaction = (status?: LSATransactionStatus) => {
    if (statusFilterValue === 'all') {
      return true;
    }

    switch (statusFilterValue) {
      case LSATransactionStatus.Completed:
        return status === LSATransactionStatus.Completed;
      case LSATransactionStatus.PendingApproval:
        return status === LSATransactionStatus.PendingApproval;
      default:
        return true;
    }
  };

  const getStatusConfig = (status?: LSATransactionStatus) => {
    switch (status) {
      case LSATransactionStatus.Completed:
        return {
          label: t("lifestyleTransactions.statusTags.completed"),
          badgeClass: "bg-white border-green-200 text-green-700",
          iconWrapperClass: "bg-green-500 text-white h-3 w-3",
          iconClass: "h-1.5 w-1.5",
          Icon: Check,
        };
      case LSATransactionStatus.PendingApproval:
        return {
          label: t("lifestyleTransactions.statusTags.pending"),
          badgeClass: "bg-white border-neutral-light text-neutral-dark",
          iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
          iconClass: "h-2.5 w-2.5",
          Icon: Clock,
        };
      case LSATransactionStatus.Incomplete:
        return {
          label: t("lifestyleTransactions.statusTags.incomplete"),
          badgeClass: "bg-white border-neutral-light text-neutral-dark",
          iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
          iconClass: "h-2.5 w-2.5",
          Icon: Clock,
        };
      case LSATransactionStatus.Denied:
        return {
          label: t("lifestyleTransactions.statusTags.denied"),
          badgeClass: "bg-white border-red-200 text-red-700",
          iconWrapperClass: "bg-red-500 text-white h-3 w-3",
          iconClass: "h-1.5 w-1.5",
          Icon: X,
        };
      default:
        return {
          label: status || t("lifestyleTransactions.statusTags.unknown"),
          badgeClass: "bg-white border-neutral-light text-neutral-dark",
          iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
          iconClass: "h-2.5 w-2.5",
          Icon: Clock,
        };
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = lifestyleCategories?.find((category: LSACategory) => category.id === categoryId);
    return category?.name || t("lifestyleTransactions.labels.unknownCategory");
  };

  const filteredTransactions = transactions.filter((transaction) =>
    shouldShowTransaction(transaction.status),
  );

  const filteredCount = filteredTransactions.length;
  const isStatusFiltered = statusFilterValue !== 'all';
  const totalCount = pagination.totalCount ?? 0;
  const hasResults = totalCount > 0;
  const safePage = Math.max(pagination.page ?? 1, 1);
  const safeLimit =
    typeof pagination.limit === "number" && pagination.limit > 0
      ? pagination.limit
      : Math.max(filteredCount, 1);
  const startItem = hasResults
    ? isStatusFiltered
      ? 1
      : (safePage - 1) * safeLimit + 1
    : 0;
  const endItem = hasResults
    ? isStatusFiltered
      ? filteredCount
      : Math.min(safePage * safeLimit, totalCount)
    : 0;

  if (isInitialLoading) {
    return (
      <Layout
        variant="dashboard"
        header={t("lifestyleTransactions.header")}
        subheader={<div className="mt-2"><span>{t("lifestyleTransactions.loading")}</span></div>}
      >
        <div className="flex items-center justify-center h-64">
          <Spinner variant="large"/>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout
        variant="dashboard"
        header={t("lifestyleTransactions.header")}
        subheader={<span>{t("lifestyleTransactions.errorDescription")}</span>}
      >
        <Card className="p-8">
          <div className="text-center text-red-600">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t("lifestyleTransactions.errorTitle")}</h3>
            <p className="text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t("lifestyleTransactions.retry")}
            </button>
          </div>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout
      variant="dashboard"
      header={t("lifestyleTransactions.header")}
      subheader={
        <div className="text-neutral-dark text-base font-normal mt-2">
          {t("lifestyleTransactions.subheader")}
        </div>
      }
    >
      <div className="flex flex-col h-full pb-24">
        <PageBreadcrumbs
          items={[
            { label: t("lifestyleTransactions.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("lifestyleTransactions.breadcrumbs.lifestyleAccount"), onClick: goToLifestyleMain },
            { label: t("lifestyleTransactions.breadcrumbs.transactions") },
          ]}
        />

        <div className="flex flex-col gap-6 border border-neutral-light bg-white rounded-2xl w-full min-w-[400px] p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-neutral-darkest uppercase tracking-wide">
                {t("lifestyleTransactions.statusFilterLabel")}
              </span>
              <div className="w-48">
                <Input
                  name="statusFilter"
                  variant="dropdown"
                  type="statusFilterDropdown"
                  options={statusFilterOptions}
                  onChange={handleStatusFilterChange}
                  placeholder={selectedStatusOption}
                  value={statusFilterValue}
                  errorMessage={undefined}
                />
              </div>
            </div>
            <div className="flex justify-start sm:justify-end">
              <ShadcnButton
                type="button"
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={isPageLoading || isInitialLoading}
                className="flex items-center justify-center w-9 h-9"
                aria-label={t("lifestyleTransactions.actions.refreshAria")}
              >
                <RotateCcw className="h-6 w-6" />
              </ShadcnButton>
            </div>
          </div>

          <div>
            <div className="relative rounded-lg border border-neutral-light overflow-hidden">
              {isPageLoading && (
                <div className="absolute inset-0 bg-white/0 flex items-center justify-center z-10">
                  <div className="bg-white border border-neutral-light rounded-lg px-5 py-4 flex flex-col items-center text-xs text-neutral-mediumDark shadow-lg">
                    <Spinner variant="large"/>
                    <span className="mt-2 font-medium whitespace-nowrap">{t("lifestyleTransactions.loadingMore")}</span>
                  </div>
                </div>
              )}
              <Table className="border-collapse min-w-full">
                <TableHeader className="table-header-background">
                  <TableRow className="border-b border-neutral-light">
                    {/* <TableHead className="pl-[10px] h-auto py-3 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                      Member
                    </TableHead> */}
                    <TableHead className="h-auto px-4 py-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.brand")}
                    </TableHead>
                    <TableHead className="h-auto px-4 py-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.category")}
                    </TableHead>
                    <TableHead className="h-auto px-4 py-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.amount")}
                    </TableHead>
                    <TableHead className="h-auto px-4 py-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.date")}
                    </TableHead>
                    <TableHead className="h-auto px-4 y-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.status")}
                    </TableHead>
                    <TableHead className="h-auto px-4 py-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.receipt")}
                    </TableHead>
                    <TableHead className="pr-[10px] h-auto px-4 py-3 text-xs font-medium table-header-text uppercase tracking-wider">
                      {t("lifestyleTransactions.tableHeaders.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((transaction, index) => {
                        const isLastRow = index === filteredTransactions.length - 1;
                        const isApproving = approvingTransactionIds.has(transaction.id);
                        const isRejecting = rejectingTransactionIds.has(transaction.id);
                        const isActiveRow =
                          isApproving ||
                          isRejecting ||
                          activeRowId === transaction.id;
                        const hoverClass =
                          isActiveRow || hasActionInFlight
                            ? isActiveRow
                              ? "hover:bg-yellow-50"
                              : ""
                            : "hover:bg-primary-lightest";
                        const firstInitial = transaction?.firstName?.charAt(0) ?? "";
                        const lastInitial = transaction?.lastName?.split(" ")?.slice(-1)[0]?.charAt(0) ?? "";
                        const initials = `${firstInitial}${lastInitial}`.toUpperCase();
                        return (
                          <TableRow
                            key={transaction.id}
                            className={`${
                              isLastRow ? "border-b-0" : "border-b border-neutral-light"
                            } ${isActiveRow ? "bg-yellow-50" : ""} ${hoverClass}`}
                          >
                            {/* <TableCell className="pl-[10px] py-2 align-top">
                              <div className="flex items-center gap-2">
                                <div
                                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                                  style={{ backgroundColor: "#FFF4E6" }}
                                >
                                  <span className="font-bold text-[10px]" style={{ color: colors.primary.medium }}>
                                    {initials || "?"}
                                  </span>
                                </div>
                                <div className="min-w-0">
                              <div className="truncate text-xs font-medium text-neutral-darkest">
                                {transaction.firstName} {transaction.lastName}
                              </div>
                              <div className="truncate text-xs text-neutral-dark">
                                {transaction.emailAddress}
                              </div>
                            </div>
                          </div>
                        </TableCell> */}
                        <TableCell className="py-2 px-4 text-xs text-neutral-dark">
                          <div className="truncate max-w-[120px]">
                            {transaction.brandName}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 px-4 text-xs text-neutral-dark">
                          <div className="truncate max-w-[100px]">
                            {getCategoryName(transaction.categoryId!)}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 px-4 text-xs font-medium text-neutral-dark">
                          ${transaction.coveredAmount.value.toLocaleString()}
                        </TableCell>
                        <TableCell className="py-2 px-4 text-xs text-neutral-dark">
                          {new Date(transaction.createdAt!).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="py-2 px-4 text-left">
                          {(() => {
                            const config = getStatusConfig(transaction.status);
                            const IconComponent = config.Icon;
                            return (
                              <Badge
                                variant="outline"
                                className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ${config.badgeClass}`}
                              >
                                <span
                                  className={`flex items-center justify-center rounded-full ${config.iconWrapperClass}`}
                                >
                                  <IconComponent className={config.iconClass} />
                                </span>
                                <span>{config.label}</span>
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="py-2 px-4">
                          <a
                            href={transaction.receiptS3URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-medium hover:text-primary-dark font-medium text-xs"
                          >
                            {t("lifestyleTransactions.receipt.view")}
                          </a>
                        </TableCell>
                        <TableCell className="pr-[10px] py-2 px-4">
                          {transaction.status === LSATransactionStatus.PendingApproval && (
                            <div className="flex items-center gap-[5px]">
                              <ApproveTransactionButton
                                onClick={() => onApproveTransaction(transaction.id, index)}
                                isLoading={isApproving}
                                disabled={isApproving || isRejecting}
                                label={t("lifestyleTransactions.actions.approve")}
                              />
                              <RejectTransactionButton
                                onClick={() => onRejectTransaction(transaction.id, index)}
                                isLoading={isRejecting}
                                disabled={isRejecting || isApproving}
                                label={t("lifestyleTransactions.actions.reject")}
                              />
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-2 pt-4 flex items-center justify-between border-t border-neutral-light">
              <div className="text-xs text-gray-700">
                {t("lifestyleTransactions.pagination.showing")}{" "}
                <span className="font-medium">{startItem}</span>{" "}
                {t("lifestyleTransactions.pagination.to")}{" "}
                <span className="font-medium">{endItem}</span>{" "}
                {t("lifestyleTransactions.pagination.of")}{" "}
                <span className="font-medium">{totalCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <ShadcnButton
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={!pagination.hasPreviousPage}
                  className="px-4 flex items-center gap-1"
                >
                  <ChevronLeft className="h-3 w-3" />
                  {t("lifestyleTransactions.pagination.previous")}
                </ShadcnButton>
                <ShadcnButton
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={
                    !pagination.hasNextPage ||
                    (statusFilterValue !== 'all' && filteredTransactions.length < pagination.limit)
                  }
                  className="px-4 flex items-center gap-1"
                >
                  {t("lifestyleTransactions.pagination.next")}
                  <ChevronRight className="h-3 w-3" />
                </ShadcnButton>
              </div>
            </div>
          </div>
        </div>
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={null}
          isContinueDisabled={false}
        />

        <ReactModal
          isOpen={!!approveErrorMessage}
          onRequestClose={onDismissApproveError}
          ariaHideApp={false}
          shouldCloseOnOverlayClick={true}
          style={{
            overlay: { backgroundColor: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 },
            content: { inset: "auto", padding: 0, border: "none", borderRadius: 12, overflow: "hidden", minWidth: 320, maxWidth: 420 }
          }}
        >
          <div className="bg-white p-6">
            <h3 className="text-lg font-semibold text-neutral-dark mb-2">{t("lifestyleTransactions.approveError.title")}</h3>
            <p className="text-sm text-neutral-dark mb-6">
              {approveErrorMessage || t("lifestyleTransactions.approveError.message")}
            </p>
            <div className="flex justify-end">
              <div className="w-24">
                <Button
                  variant="primary"
                  label={t("lifestyleTransactions.approveError.ok")}
                  onClick={onDismissApproveError}
                  stretch
                  color="primary-medium"
                />
              </div>
            </div>
          </div>
        </ReactModal>
      </div>
    </Layout>
  );
};
